import { Link } from '@tanstack/react-router'
import { ArrowRight } from 'lucide-react'

import { NAVIGATION, type NavItem } from '@/app/navigation'
import { useAuth } from '@/auth/use-auth'
import { PageHeader } from '@/components/page-header'
import { PermissionGate } from '@/components/permission-gate'
import { Card } from '@/components/ui/card'
import { ApprovalsInboxTile } from '@/features/dashboard/approvals-tile'
import { BusinessDayTile } from '@/features/dashboard/business-day-tile'
import { DashboardCapabilityGaps } from '@/features/dashboard/capability-gaps'
import { CollectionsTodayTile } from '@/features/dashboard/collections-today-tile'
import { JournalActivityTile } from '@/features/dashboard/journal-activity-tile'
import { PortfolioAtRiskTile } from '@/features/dashboard/portfolio-at-risk-tile'
import { LEDGER_PERMISSIONS } from '@/features/ledger/permissions'
import { REPORTS_PERMISSIONS } from '@/features/reports/permissions'

export function DashboardPage() {
  const { user } = useAuth()

  const modules = NAVIGATION.flatMap((section) => section.items).filter((item) => item.to !== '/')

  return (
    <>
      <PageHeader
        title={user?.displayName ? `Good day, ${user.displayName.split(' ')[0]}` : 'Dashboard'}
        description="Portfolio, arrears, and cash position for the selected branch and business date."
      />

      {/*
        Every tile below is composed from an endpoint an existing screen already calls — the
        business day, the approvals inbox, the reporting module's own PAR and collections
        register, and the ledger's journal list. Nothing here is a new aggregate: a tile whose
        permission is missing simply does not render, the same way a `ModuleCard` below does not,
        rather than showing a teaser for access the reader does not have.
      */}
      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-heading">Today</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <BusinessDayTile />
          <ApprovalsInboxTile />

          <PermissionGate permission={REPORTS_PERMISSIONS.parView}>
            <PortfolioAtRiskTile className="sm:col-span-2 xl:col-span-2" />
          </PermissionGate>

          <PermissionGate permission={REPORTS_PERMISSIONS.collectionsView}>
            <CollectionsTodayTile className="sm:col-span-2 xl:col-span-2" />
          </PermissionGate>

          <PermissionGate permission={LEDGER_PERMISSIONS.journalView}>
            <JournalActivityTile className="sm:col-span-2 xl:col-span-2" />
          </PermissionGate>

          <DashboardCapabilityGaps className="border-dashed sm:col-span-2 xl:col-span-4" />
        </div>
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-heading">Modules</h2>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {modules.map((item) => (
            <ModuleCard key={item.to} item={item} />
          ))}
        </div>
      </section>
    </>
  )
}

/**
 * Each card is gated on the same permission as its navigation entry, so the dashboard shows a
 * user exactly the modules their role reaches — and nothing they would only meet a 403 in.
 */
function ModuleCard({ item }: { item: NavItem }) {
  const Icon = item.icon

  return (
    <PermissionGate permission={item.permission} anyOf={item.anyOf}>
      <Link to={item.to} className="block">
        <Card className="flex items-center gap-3 p-3 transition-colors hover:border-border-strong hover:bg-surface-hover">
          <span
            className="flex size-8 shrink-0 items-center justify-center rounded-control bg-brand-fill-subtle text-brand-text"
            aria-hidden
          >
            <Icon className="size-4" />
          </span>
          <span className="text-sm font-medium text-heading">{item.label}</span>
          <ArrowRight className="ml-auto size-4 text-muted" aria-hidden />
        </Card>
      </Link>
    </PermissionGate>
  )
}
